import "./ticketFields.css";
import plusIcon from "../../assets/icons/plus.svg";
import { Expand, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { showSuccess, showError } from "../../components/ui/toast";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { getToken, getUser } from "../../services/authStore";

const API_URL = process.env.REACT_APP_API_URL;

function SortableRow({ field, updateField, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="expand-cell" {...attributes} {...listeners}>
        <Expand size={16} />
      </td>

      <td>
        <input
          value={field.name || ""}
          onChange={(e) => updateField(field.id, "name", e.target.value)}
        />
      </td>

      <td>
        <input
          value={field.placeholder || ""}
          onChange={(e) => updateField(field.id, "placeholder", e.target.value)}
        />
      </td>

      <td>
        <select
          value={field.type || ""}
          onChange={(e) => updateField(field.id, "type", e.target.value)}
        >
          <option value="">Select</option>
          <option value="text">Text</option>
          <option value="email">Email</option>
          <option value="number">Number</option>
          <option value="textarea">Textarea</option>
          <option value="date">Date</option>
          {/* <option value="file">File</option> */}
        </select>
      </td>

      <td>
        <select
          value={field.is_required}
          onChange={(e) =>
            updateField(field.id, "is_required", Number(e.target.value))
          }
        >
          <option value={1}>Yes</option>
          <option value={0}>No</option>
        </select>
      </td>

      <td>
        <select
          value={field.width}
          onChange={(e) =>
            updateField(field.id, "width", Number(e.target.value))
          }
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </td>

      <td>
        <select
          value={field.status}
          onChange={(e) =>
            updateField(field.id, "status", Number(e.target.value))
          }
        >
          <option value={1}>Active</option>
          <option value={0}>Inactive</option>
        </select>
      </td>

      <td>
        <Trash2
          size={16}
          className="action-icon delete"
          onClick={() => {
            if (window.confirm("Are you sure you want to delete this field?")) {
              onDelete(field.id);
            }
          }}
        />
      </td>
    </tr>
  );
}

export default function TicketField() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);

  const token = getToken();
  const user = getUser();

  // 🔹 FETCH DATA
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/settings/custom-fields/?company_id=${user?.company_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setIsFirstTime(response.data.length === 0);
        setFields(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (token && user?.company_id) {
      fetchFields();
    }
  }, [token, user]);

  // 🔹 UPDATE FIELD
  const updateField = (id, key, value) => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: value } : f)),
    );
  };

  // 🔹 ADD ROW
  const handleAddRow = () => {
    const newField = {
      id: Date.now(),
      name: "",
      type: "",
      placeholder: "",
      width: 50,
      field_order: fields.length,
      status: 1,
      is_required: 0,
      isNew: true,
    };

    setFields((prev) => [...prev, newField]);
  };

  // 🔹 DELETE ROW (LOCAL ONLY)
  const handleDeleteRow = (id) => {
    setFields((prev) => {
      const filtered = prev.filter((f) => f.id !== id);

      return filtered.map((item, index) => ({
        ...item,
        field_order: index,
      }));
    });
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      for (let field of fields) {
        if (!field.name || !field.type) {
          showError("Label and Type are required for all fields");
          setIsSaving(false);
          return;
        }
      }

      const payload = fields.map((field, index) => ({
        id: field.isNew ? 0 : field.id,
        company_id: user.company_id,
        name: field.name,
        type: field.type,
        placeholder: field.placeholder,
        width: field.width,
        field_order: index,
        status: field.status ?? 1,
        is_required: field.is_required,
        created_by: user.username,
      }));

      let response;

      if (isFirstTime) {
        // ✅ CREATE
        response = await axios.post(
          `${API_URL}/settings/custom-fields/`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        // ✅ UPDATE (FIXED)
        response = await axios.put(
          `${API_URL}/settings/custom-fields/bulk?company_id=${user.company_id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }

      // ✅ SAFE RESPONSE HANDLE
      const updatedFromServer = (response?.data || []).map((item) => ({
        ...item,
        isNew: false,
      }));

      setFields(updatedFromServer);
      setIsFirstTime(false);

      showSuccess("Fields saved successfully ");
    } catch (error) {
      console.error("SAVE ERROR:", error);

      showError(
        error?.response?.data?.message ||
          JSON.stringify(error?.response?.data) ||
          "Failed to save fields",
      );
    } finally {
      setIsSaving(false);
    }
  };
  // 🔹 DRAG REORDER
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);

        const reordered = arrayMove(items, oldIndex, newIndex);

        return reordered.map((item, index) => ({
          ...item,
          field_order: index,
        }));
      });
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="ticket-wrapper">
      <div className="ticket-container">
        <div className="ticket-header">
          <div className="ticket-left">
            <span className="custom-title">Custom Fields</span>
            <span className="ticket-subtitle">Drag & drop to change order</span>
          </div>

          <div className="add-btn" onClick={handleAddRow}>
            <img src={plusIcon} alt="add" />
          </div>
        </div>

        <div className="field-table">
          <div className="table-wrapper">
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Label</th>
                      <th>Placeholder</th>
                      <th>Type</th>
                      <th>Required</th>
                      <th>Width</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="no-data">
                          No Data Available
                        </td>
                      </tr>
                    ) : (
                      fields.map((field) => (
                        <SortableRow
                          key={field.id}
                          field={field}
                          updateField={updateField}
                          onDelete={handleDeleteRow}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        <div style={{ textAlign: "right", padding: "20px" }}>
          <button className="save-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
